import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { useSignalRContext } from '../components/Contexts/SignalRContext'
import { convertDatesToUTC } from '../ajax/controller/AjaxController'
import { offlineDb, type MutationQueueItem } from './db'
import { beginSync, endSync, reportSynced } from './syncStatus'
import { store } from '../state/store'
import { clearProvisionalShift } from '../state/reducers/shiftReducer'
import { refreshLatestShift } from './refreshLatestShift'
import { patchCachedEntityId, patchCachedRowVersion } from './patchCachedRowVersion'
import { syncAjax } from '../ajax/SyncAjax'
import { SyncOperation, SyncStatus } from '../DTOs/Sync/SyncDtos'
import type { SyncRequest, SyncResponse } from '../DTOs/Sync/SyncDtos'

function operationCode(op: MutationQueueItem['operation']) {
  if (op === 'create') return SyncOperation.Create
  if (op === 'update') return SyncOperation.Update
  return SyncOperation.Delete
}

function buildSyncRequest(items: MutationQueueItem[]): SyncRequest {
  const request: SyncRequest = { stoppages: [], loadings: [], diggingPatterns: [], patternBlasts: [], completeShifts: [], geoRehandles: [], operationLocations: [], engineHours: [] }
  for (const item of items) {
    const base = {
      clientId: item.clientId,
      operation: operationCode(item.operation),
      shiftId: item.shiftId,
      rowVersion: item.rowVersion,
      // Note: normalise on the way out as well as on the way in. A queue written before
      // convertDatesToUTC recognised a revived Luxon object holds that object's internals - {ts,
      // _zone, loc, c, o} - where a date should be, and the API cannot bind it: one such field in one
      // row failed the whole request with a 400 and stranded every other change with it. Cleaning here
      // means a queue already carrying one goes up without the crew re-entering anything.
      data: convertDatesToUTC(item.data),
    }
    if (item.entity === 'stoppage') {
      const { plant: _p, stoppageType: _st, stoppageSubType: _sst, location: _l, ...safeData } = (base.data ?? {}) as any
      request.stoppages.push({ ...base, data: safeData, stoppageId: item.entityId })
    } else if (item.entity === 'loading') {
      request.loadings.push({ ...base, loadingId: item.entityId })
    } else if (item.entity === 'diggingPattern') {
      request.diggingPatterns.push({ ...base, diggingPatternId: item.entityId })
    } else if (item.entity === 'patternBlast') {
      request.patternBlasts.push({ ...base })
    } else if (item.entity === 'completeShift') {
      request.completeShifts.push({ clientId: item.clientId, shiftId: item.shiftId })
    } else if (item.entity === 'operationLocation') {
      const d = (item.data ?? {}) as { operationId?: number; locationId?: number; operationPlantId?: number }
      request.operationLocations.push({
        clientId: item.clientId,
        operationId: d.operationId ?? item.entityId ?? 0,
        locationId: d.locationId ?? 0,
        shiftId: item.shiftId,
        operationPlantId: d.operationPlantId,
      })
    } else if (item.entity === 'engineHours') {
      const d = (item.data ?? {}) as { operationId?: number; startEngineHours?: number; endEngineHours?: number; operationPlantId?: number }
      request.engineHours.push({
        clientId: item.clientId,
        operationId: d.operationId ?? item.entityId ?? 0,
        shiftId: item.shiftId,
        startEngineHours: d.startEngineHours,
        endEngineHours: d.endEngineHours,
        operationPlantId: d.operationPlantId,
      })
    } else if (item.entity === 'geoRehandle') {
      // Note: shiftId travels with a rehandle so the server can resolve a provisional operation id
      // (negative, from a shift created offline) to the real operation for that shift and plant.
      request.geoRehandles.push({ clientId: item.clientId, operation: operationCode(item.operation), geoRehandleId: item.entityId, rowVersion: item.rowVersion, shiftId: item.shiftId, data: item.data })
    }
  }
  return request
}

// Note: both of these run inside the caller's mutationQueue transaction.
async function remapQueuedPatternId(tempId: number, realId: number): Promise<void> {
  const queued = await offlineDb.mutationQueue.where('entity').equals('loading').toArray()

  for (const item of queued) {
    const data = item.data as { diggingPatternId?: number } | undefined
    if (!data || data.diggingPatternId !== tempId) continue
    await offlineDb.mutationQueue.update(item.id!, { data: { ...data, diggingPatternId: realId } })
  }
}

// Note: an edit or delete made against a row created offline still carries the row's temp id. Once
// the create has synced, that id no longer identifies anything on the server - the update reached it
// as an id that does not exist, which surfaced as "Sequence contains no elements" from a repository
// that expects exactly one row. Point the remaining queue entries at the real id instead.
async function remapQueuedEntityId(
  entity: MutationQueueItem['entity'],
  tempId: number,
  realId: number,
): Promise<void> {
  const queued = await offlineDb.mutationQueue
    .where('[entity+entityId]')
    .equals([entity, tempId])
    .toArray()

  for (const item of queued) {
    const data = item.data && typeof item.data === 'object'
      ? { ...(item.data as Record<string, unknown>) }
      : item.data

    if (data && typeof data === 'object') {
      for (const idField of ['loadingId', 'stoppageId', 'geoRehandleId', 'diggingPatternId']) {
        if ((data as Record<string, unknown>)[idField] === tempId) {
          (data as Record<string, unknown>)[idField] = realId
        }
      }
    }

    await offlineDb.mutationQueue.update(item.id!, { entityId: realId, data })
  }
}

export function useSyncWorker() {
  const queryClient = useQueryClient()
  const { connection } = useSignalRContext()
  const pendingCount = useLiveQuery(() => offlineDb.mutationQueue.count(), []) ?? 0
  const isSyncingRef = useRef(false)

  const runSync = useCallback(async () => {
    if (isSyncingRef.current) return
    isSyncingRef.current = true
    try {
      // Note: read the queue fresh from Dexie rather than from the useLiveQuery-backed
      // itemsRef, which lags a tick behind writes. A stale snapshot here re-sends items
      // that a previous run already synced-and-deleted, creating duplicate server rows.
      const snapshot = await offlineDb.mutationQueue.orderBy('createdAt').toArray()

      // Note: publish what is being sent so the UI can say "syncing 12 of 86" rather than showing
      // nothing at all while a shift's work goes up, which read as the queue having been lost.
      if (snapshot.length) beginSync(snapshot.length)

      // Note: the provisional-shift marker is only justified by a queued completion. If we are online
      // and no completion is queued, either it synced (cleared already) or the queue is gone - the
      // marker is stale, and it keeps offering a shift that will never exist. The marker lives in
      // localStorage, so clearing the offline database on its own leaves it behind.
      if (navigator.onLine
        && store.getState().shift.provisionalShiftDate
        && !snapshot.some(item => item.entity === 'completeShift')) {
        store.dispatch(clearProvisionalShift())
      }

      if (snapshot.length === 0) return

      const staleEntities = new Set<MutationQueueItem['entity']>()

      // Note: apply the server's per-item results - drain applied items, record anything else
      // (conflict / shift-locked / not-found / error) as a sync issue - then remove them all from
      // the queue.
      const processResults = async (results: SyncResponse['results'], items: MutationQueueItem[]) => {
        reportSynced(results.length)
        const byClientId = new Map(items.map(i => [i.clientId, i]))
        await offlineDb.transaction('rw', offlineDb.mutationQueue, offlineDb.syncIssues, async () => {
          for (const result of results) {
            const item = byClientId.get(result.clientId)
            if (!item) continue
            staleEntities.add(item.entity)

            // Note: a load queued against a pattern created offline carries the pattern's negative
            // temp id. The server only maps temp ids for patterns created in the same request, so a
            // load left in the queue after its pattern synced - a split batch, or loads added later -
            // reached the server with an id that does not exist ("Sequence contains no elements").
            // Rewrite the queue as soon as the real id is known.
            if (item.operation === 'create'
              && result.status === SyncStatus.Applied
              && typeof result.serverId === 'number'
              && typeof item.entityId === 'number'
              && item.entityId < 0) {
              if (item.entity === 'diggingPattern') {
                await remapQueuedPatternId(item.entityId, result.serverId)
              }
              await remapQueuedEntityId(item.entity, item.entityId, result.serverId)
              // Note: and in the cached rows, so the row on screen stops carrying a temp id.
              patchCachedEntityId(item.entity, item.entityId, result.serverId)
            }

            // Note: keep the cached row's rowVersion in step with the server's after an applied
            // update, or the next offline edit of that row is rejected as a conflict against a
            // version only this client is still holding.
            if (item.operation === 'update' && result.status === SyncStatus.Applied) {
              patchCachedRowVersion(item.entity, item.entityId, result.serverRowVersion)
            }
            if (result.status !== SyncStatus.Applied) {
              await offlineDb.syncIssues.add({
                clientId: result.clientId,
                entity: item.entity,
                operation: item.operation,
                status: result.status,
                entityId: item.entityId,
                shiftId: item.shiftId,
                serverState: result.serverState,
                clientData: item.data,
                createdAt: Date.now(),
              })
            }
            await offlineDb.mutationQueue.where('clientId').equals(result.clientId).delete()
          }
        })
      }

      // Note: the server rejected an item at the request level - a 4xx/5xx that produced no
      // per-item results (e.g. a payload it can't even deserialise). Record it as a sync issue and
      // drop it from the queue so one un-appliable change can't block every other queued change
      // forever (the failure mode that previously made whole batches vanish silently).
      const quarantine = async (item: MutationQueueItem, code?: number) => {
        staleEntities.add(item.entity)
        await offlineDb.transaction('rw', offlineDb.mutationQueue, offlineDb.syncIssues, async () => {
          await offlineDb.syncIssues.add({
            clientId: item.clientId,
            entity: item.entity,
            operation: item.operation,
            status: SyncStatus.Error,
            entityId: item.entityId,
            shiftId: item.shiftId,
            serverState: `The server rejected this change${code ? ` (HTTP ${code})` : ''}, so it could not be applied. It has been removed from the queue - please re-enter it.`,
            clientData: item.data,
            createdAt: Date.now(),
          })
          await offlineDb.mutationQueue.where('clientId').equals(item.clientId).delete()
        })
      }

      try {
        const response = await syncAjax.sync(buildSyncRequest(snapshot))
        if (response.success && response.data) {
          await processResults(response.data.results, snapshot)
        } else if (snapshot.length === 1) {
          // Note: the whole request was rejected and there is only one item, so it is the offender.
          await quarantine(snapshot[0], response.errors?.[0]?.code)
        } else {
          // Note: the whole batch was rejected. Re-send each item on its own so the good ones still
          // sync and only the offending item(s) are quarantined. Server-side idempotency (clientId
          // dedup) makes re-sending safe even if part of the failed batch had already applied.
          for (const item of snapshot) {
            const single = await syncAjax.sync(buildSyncRequest([item]))
            if (single.success && single.data) {
              await processResults(single.data.results, [item])
            } else {
              await quarantine(item, single.errors?.[0]?.code)
            }
          }
        }
      } catch {
        // Note: a thrown result is a network/connectivity failure (not a server rejection), so
        // leave the queue untouched for the next online/retry attempt rather than quarantining.
        return
      }

      // Note: invalidation is the reconciliation step. Refetching replaces any optimistic
      // temp-id rows with the real server rows, so every query key that an entity's optimistic
      // write can touch must be invalidated here or its temp rows linger until a manual refresh.
      for (const entity of staleEntities) {
        if (entity === 'stoppage') {
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'operations'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'stoppages'] })
        } else if (entity === 'loading') {
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'truckLoads'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggingLoadingSummary'] })
        } else if (entity === 'diggingPattern') {
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggerLocations'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggingLoadingSummary'] })
        } else if (entity === 'operationLocation' || entity === 'engineHours') {
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'operations'] })
          await queryClient.invalidateQueries({ queryKey: ['operations'] })
        } else if (entity === 'geoRehandle') {
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'geology', 'rehandles'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'geology', 'crushing'] })
        } else if (entity === 'completeShift') {
          // Note: the completion has run for real, so the shift exists server-side and its lock
          // state comes from the server again. Drop the provisional marker that kept it editable,
          // and pick up the shift the completion created as the new latest - otherwise the marker
          // still points at the shift that was just completed and the crew cannot complete the
          // next one offline.
          store.dispatch(clearProvisionalShift())
          await refreshLatestShift()
          // Note: a completion creates the shift and everything hanging off it, and the provisional
          // seeding left placeholder entries behind - empty lists for the new shift's stoppages,
          // rehandles, crushing and digging summary, and provisional operation rows. Those are
          // guesses; now that the real rows exist every cached table has to be thrown away, not just
          // the operations. Leaving the seeded empty digging summary in place is what kept the
          // Production list reporting no digging entries for a shift that had 17 of them.
          await queryClient.invalidateQueries({ queryKey: ['dataTable'] })
          await queryClient.invalidateQueries({ queryKey: ['chatree'] })
          await queryClient.invalidateQueries({ queryKey: ['production'] })
          await queryClient.invalidateQueries({ queryKey: ['geology'] })
          // Note: the date picker's existence check, so the new shift's date does not vanish from the
          // calendar while the pre-completion answer is still cached.
          await queryClient.invalidateQueries({ queryKey: ['shiftExistence'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'operations'] })
          await queryClient.invalidateQueries({ queryKey: ['operations'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'production'] })
          await queryClient.invalidateQueries({ queryKey: ['production'] })
        } else {
          // Note: patternBlast and any future entity — refresh the blast options and the
          // location/production tables that reference blasts.
          await queryClient.invalidateQueries({ queryKey: ['references', 'blastmasterIds'] })
          await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggerLocations'] })
        }
      }
    } finally {
      isSyncingRef.current = false
      endSync()
    }
  }, [queryClient])

  useEffect(() => {
    let active = true
    const trigger = () => { if (active && navigator.onLine) runSync() }

    trigger()
    window.addEventListener('online', trigger)
    connection?.onreconnected(() => { if (active) runSync() })

    // Note: while items are queued, retry on an interval so a single failed sync (API restart,
    // network blip) does not leave the queue stuck until the user reloads. The interval is torn
    // down as soon as the queue drains (pendingCount is a dependency, so this effect re-runs).
    const retry = pendingCount > 0 ? setInterval(trigger, 15000) : null

    return () => {
      active = false
      window.removeEventListener('online', trigger)
      if (retry) clearInterval(retry)
    }
    // Note: pendingCount is a dependency so a newly-enqueued mutation kicks off a sync
    // attempt immediately when online, instead of waiting for the next reconnect event.
  }, [connection, runSync, pendingCount])
}
